import type { FC } from "react";
import { useCallback } from "react";
import { useMediaQuery } from "react-responsive";

import { ResponsiveImage } from "@ui/components/ResponsiveImage";

import type {
  FlavorFinderAdditionalQuestionDetails,
  FlavorFinderOptionalQuestionTransitions,
  Step,
} from "../../types/FlavorFinderTypes";
import { FlavorFinderBackButton, FlavorFinderNextButton, FlavorFinderSkipButton } from "../Buttons/FlavorFinderButtons";
import { FlavorFinderHeader } from "../Header/FlavorFinderHeader";
import { useFlavorFinderContext } from "../Provider/FlavorFinderContextProvider";

import "./FlavorFinderOptionalQuestion.scss";

type FlavorFinderAdditionalProps = {
  question: FlavorFinderOptionalQuestionTransitions;
  nextCtaLabel: string;
  backCtaLabel: string;
  skipCtaLabel: string;
  selectionIndicator?: string;
  hideNextButton?: boolean;
};

export const FlavorFinderOptionalQuestion: FC<FlavorFinderAdditionalProps> = ({
  question,
  nextCtaLabel,
  backCtaLabel,
  skipCtaLabel,
  selectionIndicator,
  hideNextButton = false,
}) => {
  const { question: questionDetails, possibleAnswers } = question;
  const { userJourney, setUserJourney, setCurrentView, handlePreviousStepClick, setCurrentQuestionIndex } =
    useFlavorFinderContext();

  const isMobile = useMediaQuery({ maxWidth: 479 });

  const selectedAnswerIdForCurrentQuestion = userJourney.find(
    (step) => step.question?.question?.id === questionDetails.id
  );
  const handleNextStepClick = useCallback(() => {
    setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
    setUserJourney([...userJourney, { view: "consent" }]);
    setCurrentView("consent");
  }, [setCurrentView]);

  const handleAnswerSelect = (answerId: string) => () => {
    const updatedJourney = [...userJourney];
    const currentStepIndex = updatedJourney.findIndex((step) => step.question?.question?.id === questionDetails.id);
    const selectedAnswer = possibleAnswers.find((answer) => answer.id === answerId);

    if (currentStepIndex > -1) {
      updatedJourney[currentStepIndex].answer_id = answerId;
      updatedJourney[currentStepIndex].reference_name = selectedAnswer?.referenceName;
      updatedJourney[currentStepIndex].local_translation = selectedAnswer?.localTranslation;
    } else {
      updatedJourney.push({
        view: "optional",
        question: question,
        answer_id: answerId,
        reference_name: selectedAnswer?.referenceName,
        local_translation: selectedAnswer?.localTranslation,
      });
    }

    setUserJourney(updatedJourney);
    if (hideNextButton) {
      handleNextStepClick();
    }
  };

  const findStepIndex = (journey: Step[], questionId: string): number => {
    const index = journey.findIndex((step) => step.question?.question?.id === questionId);
    if (index === -1) {
      throw new Error(`Step not found for question ID: ${questionId}`);
    }
    return index;
  };

  const handleSkipClick = useCallback(() => {
    setCurrentQuestionIndex((prevIndex) => prevIndex + 1);

    setUserJourney((prevJourney) => {
      try {
        const updatedJourney = [...prevJourney];
        const currentStepIndex = findStepIndex(updatedJourney, questionDetails.id);

        if (updatedJourney[currentStepIndex]) {
          delete updatedJourney[currentStepIndex].answer_id;
        }

        return [...updatedJourney, { view: "consent" }];
      } catch {
        console.error("Error updating journey");
        return prevJourney;
      }
    });

    setCurrentView("consent");
  }, [questionDetails.id]);

  return (
    <div
      className="flavorfinder__questions-wrapper flavorfinder__questions-wrapper--additional"
      data-testid="flavor-finder-additional-question"
    >
      {isMobile && <FlavorFinderBackButton onClick={handlePreviousStepClick} label={backCtaLabel} />}
      <div className={"flavorfinder__question-container"}>
        <div className="flavorfinder__question">
          <FlavorFinderHeader infoText={`${questionDetails.indicatorOverrideLabel}`}>
            {!isMobile && <FlavorFinderBackButton onClick={handlePreviousStepClick} label={backCtaLabel} />}
          </FlavorFinderHeader>
          <div className="flavorfinder__additional-question-content">
            {questionDetails.image ? (
              <ResponsiveImage
                alt={questionDetails.alt}
                largeSrc={questionDetails.image}
                mediumSrc={questionDetails.mediumImage}
                smallSrc={questionDetails.smallImage}
                className="responsive-image__container"
              />
            ) : null}
            <div className={"flavorfinder__panel"}>
              <h3 className={"flavorfinder__panel-header"}>{questionDetails.title}</h3>
              {selectionIndicator && <p className="flavorfinder__panel-subheader">{selectionIndicator}</p>}
              <div className="flavorfinder__inputs">
                {possibleAnswers.map((answer: FlavorFinderAdditionalQuestionDetails) => {
                  return (
                    <div
                      className="flavorfinder__input-wrapper"
                      key={answer.id}
                      onClick={handleAnswerSelect(answer.id)}
                    >
                      <input
                        type="radio"
                        id={answer.id}
                        name="answerGroup"
                        value={answer.title}
                        checked={answer.id === selectedAnswerIdForCurrentQuestion?.answer_id}
                      />
                      <label htmlFor={answer.id}>{answer.title}</label>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flavorfinder__footer">
            <FlavorFinderNextButton
              onClick={handleNextStepClick}
              label={nextCtaLabel}
              disabled={selectedAnswerIdForCurrentQuestion?.answer_id === undefined}
              hidden={hideNextButton}
            />
            <FlavorFinderSkipButton label={skipCtaLabel} onClick={handleSkipClick} />
          </div>
        </div>
      </div>
    </div>
  );
};
